import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvestorUpdate() {
    const [params] = useSearchParams();
    const [update, setUpdate] = useState(null);
    const [error, setError] = useState("");
    const [visitStart] = useState(Date.now());
    const [question, setQuestion] = useState({ email: "", display_name: "", body: "" });
    const [asked, setAsked] = useState(false);

    const uid = params.get("u");
    const token = params.get("t");

    useEffect(() => {
        if (!uid || !token) { setError("Missing update or token"); return; }
        axios.get(`${BASE}/api/public/investor-updates/${uid}?t=${encodeURIComponent(token)}`)
            .then(({ data }) => setUpdate(data))
            .catch((err) => setError(err.response?.data?.detail || "Link is invalid or expired."));
    }, [uid, token]);

    // Dwell tracking ping every 20s, and beacon on unload
    useEffect(() => {
        if (!update) return;
        const send = (section) => {
            const dwell_ms = Date.now() - visitStart;
            try {
                axios.post(`${BASE}/api/public/investor-updates/${uid}/ping`, { token, section, dwell_ms });
            } catch {} // ignore
        };
        const iv = setInterval(() => send("visible"), 20000);
        const onHide = () => send("leave");
        window.addEventListener("beforeunload", onHide);
        return () => { clearInterval(iv); window.removeEventListener("beforeunload", onHide); send("leave"); };
    }, [update, uid, token, visitStart]);

    const submitQuestion = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${BASE}/api/public/investor-updates/${uid}/question`, { token, ...question });
            setAsked(true);
            toast.success("Question sent");
        } catch { toast.error("Could not submit"); }
    };

    if (error) return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div className="max-w-md text-center" data-testid="public-update-error">
                <div className="text-lg tracking-tight mb-2">Unable to load update</div>
                <div className="text-sm text-muted-foreground">{error}</div>
            </div>
        </div>
    );
    if (!update) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

    const proj = update.project || {};
    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="public-investor-update">
            <div className="max-w-3xl mx-auto p-6 md:p-10">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Investor update</div>
                <h1 className="text-3xl tracking-tight mt-2" data-testid="update-title">{update.title}</h1>
                <div className="text-sm text-muted-foreground mt-1">{update.project_name} · Published {update.published_at?.slice(0, 10)}</div>

                <div className="grid grid-cols-3 gap-3 mt-6">
                    <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Phase</div><div className="text-sm mt-1">{(proj.current_phase_key || "—").replace(/_/g, " ")}</div></div>
                    <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Progress</div><div className="text-sm mt-1">{Math.round(proj.completion_pct || 0)}%</div></div>
                    <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Budget</div><div className="text-sm mt-1">${Number(proj.total_budget || 0).toLocaleString()}</div></div>
                </div>

                <div className="mt-8 whitespace-pre-wrap text-sm leading-relaxed" data-testid="update-body">{update.body}</div>

                <div className="mt-10 border-t border-border pt-6">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Ask a question</div>
                    {asked ? (
                        <div className="border border-border p-4 text-sm" data-testid="public-question-thanks">Thanks — the sponsor will respond by email.</div>
                    ) : (
                        <form onSubmit={submitQuestion} className="space-y-3" data-testid="public-question-form">
                            <div className="grid md:grid-cols-2 gap-3">
                                <input required type="email" placeholder="Your email" value={question.email} onChange={(e) => setQuestion({ ...question, email: e.target.value })} className="border border-border bg-background px-3 py-2 text-sm" data-testid="public-question-email" />
                                <input placeholder="Your name (optional)" value={question.display_name} onChange={(e) => setQuestion({ ...question, display_name: e.target.value })} className="border border-border bg-background px-3 py-2 text-sm" />
                            </div>
                            <textarea required rows={3} placeholder="Your question…" value={question.body} onChange={(e) => setQuestion({ ...question, body: e.target.value })} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="public-question-body" />
                            <button className="bg-foreground text-background px-4 py-2 text-sm" data-testid="public-question-submit">Send question</button>
                        </form>
                    )}
                </div>

                <div className="mt-16 pt-6 border-t border-border text-xs text-muted-foreground">
                    Sensitive — do not forward. Contact the sponsor for account access.
                </div>
            </div>
        </div>
    );
}
